import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { can, useAuth } from '@/shared/auth';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useOnline } from '@/shared/hooks/useOnline';
import { PRIVILEGES } from '@stayon/auth-core';
import { CleaningScopeRequired } from '../CleaningScopeRequired';
import { parseCleaningDate, todayInTimeZone } from '../calendar/cleaningCalendarHelpers';
import { CleaningTaskDetailPanel } from '../day/CleaningTaskDetailPanel';
import type { CleaningDayFilter } from '../day/cleaningDayModel';
import { ManualTaskModal } from '../ManualTaskModal';
import { useCleaningBoardQueries } from '../useCleaningBoardQueries';
import { useCleaningScope } from '../useCleaningScope';
import { BoardBody } from './BoardBody';
import { BoardHeader } from './BoardHeader';
import type { CleaningBoardView } from './boardModel';
import { useBoardMutations } from './useBoardMutations';

/**
 * Aufgaben und Woche für alle, die zuteilen oder zusehen (Leitung, Verwaltung), und die Woche der
 * Reinigung. Datum, Ansicht, Status und Person stehen in der Adresse.
 */
export function CleaningBoardPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_admin/cleaning' });
  const { user } = useAuth();
  const scope = useCleaningScope();
  const { group } = scope;
  const today = group ? todayInTimeZone(group.timezone) : '1970-01-01';
  const date = parseCleaningDate(search.date, today);
  const view: CleaningBoardView = search.view ?? 'list';
  const filter: CleaningDayFilter = search.status ?? 'all';
  const mayManage = can(user, PRIVILEGES.cleaningAssignmentsManage);
  const assignee = mayManage ? (search.assignee ?? '') : '';
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedTrigger = useRef<HTMLElement | null>(null);
  const desktop = useMediaQuery('(min-width: 1280px)');
  const queries = useCleaningBoardQueries(group, date, view, assignee, selectedId);
  const mutations = useBoardMutations();
  const online = useOnline();
  const selectedTask =
    queries.dayTasks.find((task) => task.id === selectedId) ?? queries.detail.data?.summary ?? null;

  const closeDetail = () => {
    setSelectedId(null);
    requestAnimationFrame(() => selectedTrigger.current?.focus());
  };
  const setSearch = (patch: Partial<typeof search>) => {
    closeDetail();
    void navigate({ to: '/cleaning', search: { ...search, ...patch } });
  };
  const select = (id: string, trigger: HTMLElement) => {
    selectedTrigger.current = trigger;
    setSelectedId(id);
  };
  const changeGroup = (id: string | null) => {
    closeDetail();
    scope.setGroupId(id);
  };

  if (!group)
    return (
      <CleaningScopeRequired
        groups={scope.groups}
        loading={scope.isLoading}
        error={scope.isError}
        select={changeGroup}
        retry={scope.retry}
      />
    );
  return (
    <div className="mx-auto w-full max-w-[96rem]">
      <div
        className={
          selectedTask && desktop ? 'grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]' : ''
        }
      >
        <main className="min-w-0">
          <BoardHeader
            view={view}
            date={date}
            today={today}
            filter={filter}
            assignee={assignee}
            queries={queries}
            scope={scope}
            mayManage={mayManage}
            onSearch={setSearch}
            onGroup={changeGroup}
            onCreate={() => mutations.setCreateOpen(true)}
          />
          <BoardBody
            view={view}
            date={date}
            today={today}
            filter={filter}
            queries={queries}
            selectedId={selectedId}
            onSelect={select}
            onClaim={(task) => mutations.claim.mutate(task)}
            claimingId={mutations.claim.isPending ? mutations.claim.variables?.id : undefined}
            currentUserId={user?.userId}
            personFiltered={assignee !== ''}
            online={online}
          />
        </main>
        <CleaningTaskDetailPanel
          task={selectedTask}
          detail={queries.detail}
          date={date}
          desktop={desktop}
          offline={!online}
          onOpenChange={(open) => {
            if (!open) closeDetail();
          }}
          onClaim={(task) => mutations.claim.mutate(task)}
          claimingId={mutations.claim.isPending ? mutations.claim.variables?.id : undefined}
        />
      </div>
      <ManualTaskModal
        open={mutations.createOpen}
        onOpenChange={mutations.setCreateOpen}
        onCreate={(vars) => mutations.create.mutate(vars)}
        isPending={mutations.create.isPending}
        apartments={group.apartments}
        date={date}
        offline={!online}
      />
    </div>
  );
}
